import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import ExcelImportDialog from '@/components/ExcelImportDialog';
import { PageHeader } from '@/components/PageHeader';
import { Plus, Trash2, Copy, CheckCircle, Edit2, Save, X, AlertTriangle, FileCheck, Table2, Settings, Upload } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status labels
const TABLE_STATUS = {
  DRAFT: { label: 'Bozza', color: 'bg-gray-100 text-gray-800' },
  ACTIVE: { label: 'Attiva', color: 'bg-green-100 text-green-800' },
  ARCHIVED: { label: 'Archiviata', color: 'bg-yellow-100 text-yellow-800' }
};

// Expense categories for allocation rules
const EXPENSE_CATEGORIES = {
  ENERGIA: 'Energia elettrica',
  ACQUA: 'Acqua',
  GAS: 'Gas',
  RISCALDAMENTO: 'Riscaldamento',
  MANUTENZIONE_ORDINARIA: 'Manutenzione ordinaria',
  MANUTENZIONE_STRAORDINARIA: 'Manutenzione straordinaria',
  PULIZIA: 'Pulizia',
  ASSICURAZIONE: 'Assicurazione',
  ASCENSORE: 'Ascensore',
  PORTIERE: 'Portiere',
  AMMINISTRAZIONE: 'Amministrazione',
  ALTRO: 'Altro'
};

export const Millesimi = () => {
  const { token, selectedCondominium } = useAuth();
  const userRole = selectedCondominium?.role || 'RESIDENT';
  const condoId = selectedCondominium?.condominium?.id;
  const canEdit = ['ADMIN', 'ACCOUNTANT'].includes(userRole);
  
  // Data state
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableEntries, setTableEntries] = useState([]);
  const [allocationRules, setAllocationRules] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [isNewTableOpen, setIsNewTableOpen] = useState(false);
  const [isPublishOpen, setIsPublishOpen] = useState(false);
  const [isRuleDialogOpen, setIsRuleDialogOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isAddEntryOpen, setIsAddEntryOpen] = useState(false);
  
  // Edit mode for entries
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  
  // Forms
  const [tableForm, setTableForm] = useState({ name: '', description: '' });
  const [publishDate, setPublishDate] = useState(new Date().toISOString().split('T')[0]);
  const [ruleForm, setRuleForm] = useState({ expense_category: '', table_id: '', description: '' });
  const [newEntryForm, setNewEntryForm] = useState({ unit_id: '', millesimi: '' });

  // Fetch functions
  const fetchTables = useCallback(async () => {
    if (!condoId) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/millesimal-tables`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTables(data);
        if (data.length > 0 && !selectedTable) {
          setSelectedTable(data[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching tables:', error);
    }
  }, [condoId, token, selectedTable]);

  const fetchTableDetails = useCallback(async (tableId) => {
    if (!condoId || !tableId) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/millesimal-tables/${tableId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTableEntries(data.entries || []);
        setSelectedTable(data.table);
      }
    } catch (error) {
      console.error('Error fetching table details:', error);
    }
  }, [condoId, token]);

  const fetchAllocationRules = useCallback(async () => {
    if (!condoId) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/allocation-rules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setAllocationRules(await res.json());
    } catch (error) {
      console.error('Error fetching rules:', error);
    }
  }, [condoId, token]);

  const fetchUnits = useCallback(async () => {
    if (!condoId) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/units`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setUnits(await res.json());
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  }, [condoId, token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchTables(), fetchAllocationRules(), fetchUnits()]);
      setLoading(false);
    };
    loadData();
  }, [fetchTables, fetchAllocationRules, fetchUnits]);

  useEffect(() => {
    if (selectedTable?.id) {
      fetchTableDetails(selectedTable.id);
    }
  }, [selectedTable?.id, fetchTableDetails]);

  // Create table
  const handleCreateTable = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/millesimal-tables`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(tableForm)
      });
      if (res.ok) {
        toast.success('Tabella creata');
        setIsNewTableOpen(false);
        setTableForm({ name: '', description: '' });
        fetchTables();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore creazione tabella');
      }
    } catch (error) {
      toast.error('Errore creazione tabella');
    }
  };

  // Duplicate table
  const handleDuplicateTable = async (tableId) => {
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/millesimal-tables/${tableId}/duplicate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const newTable = await res.json();
        toast.success(`Tabella duplicata (v${newTable.version})`);
        fetchTables();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore duplicazione');
      }
    } catch (error) {
      toast.error('Errore duplicazione');
    }
  };

  // Publish table
  const handlePublishTable = async () => {
    if (!selectedTable) return;
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/millesimal-tables/${selectedTable.id}/publish?valid_from=${publishDate}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        toast.success('Tabella pubblicata');
        setIsPublishOpen(false);
        fetchTables();
        fetchTableDetails(selectedTable.id);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore pubblicazione');
      }
    } catch (error) {
      toast.error('Errore pubblicazione');
    }
  };

  // Delete table
  const handleDeleteTable = async (tableId) => {
    if (!window.confirm('Eliminare questa tabella?')) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/millesimal-tables/${tableId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Tabella eliminata');
        setSelectedTable(null);
        fetchTables();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore eliminazione');
      }
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  // Populate table with all units
  const handlePopulateTable = async () => {
    if (!selectedTable) return;
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/millesimal-tables/${selectedTable.id}/populate?default_value=0`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const result = await res.json();
        toast.success(result.message);
        fetchTableDetails(selectedTable.id);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore');
    }
  };

  // Add entry
  const handleAddEntry = async (e) => {
    e.preventDefault();
    if (!selectedTable) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/millesimal-tables/${selectedTable.id}/entries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          unit_id: newEntryForm.unit_id,
          millesimi: parseFloat(newEntryForm.millesimi)
        })
      });
      if (res.ok) {
        toast.success('Unità aggiunta');
        setIsAddEntryOpen(false);
        setNewEntryForm({ unit_id: '', millesimi: '' });
        fetchTableDetails(selectedTable.id);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore');
    }
  };

  // Update entry
  const handleUpdateEntry = async (entryId) => {
    if (!selectedTable) return;
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/millesimal-tables/${selectedTable.id}/entries/${entryId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ millesimi: parseFloat(editingValue) })
        }
      );
      if (res.ok) {
        toast.success('Valore aggiornato');
        setEditingEntryId(null);
        fetchTableDetails(selectedTable.id);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore');
    }
  };

  // Delete entry
  const handleDeleteEntry = async (entryId) => {
    if (!selectedTable) return;
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/millesimal-tables/${selectedTable.id}/entries/${entryId}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        toast.success('Voce eliminata');
        fetchTableDetails(selectedTable.id);
      }
    } catch (error) {
      toast.error('Errore');
    }
  };

  // Create allocation rule
  const handleCreateRule = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/allocation-rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(ruleForm)
      });
      if (res.ok) {
        toast.success('Regola creata');
        setIsRuleDialogOpen(false);
        setRuleForm({ expense_category: '', table_id: '', description: '' });
        fetchAllocationRules();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore');
    }
  };

  // Delete allocation rule
  const handleDeleteRule = async (ruleId) => {
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/allocation-rules/${ruleId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Regola eliminata');
        fetchAllocationRules();
      }
    } catch (error) {
      toast.error('Errore');
    }
  };

  // Calculate totals
  const totalMillesimi = tableEntries.reduce((sum, e) => sum + (e.millesimi || 0), 0);
  const isValid = Math.abs(totalMillesimi - 1000) < 0.01;
  const canEditEntries = userRole === 'ADMIN' && selectedTable?.status === 'DRAFT';
  const isResident = userRole === 'RESIDENT';

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader title="Millesimi" subtitle="Tabelle millesimali e riparti" />

      <Tabs defaultValue="tables" className="space-y-4">
        <TabsList>
          <TabsTrigger value="tables">
            <Table2 className="h-4 w-4 mr-2" />
            Tabelle
          </TabsTrigger>
          <TabsTrigger value="rules">
            <Settings className="h-4 w-4 mr-2" />
            Regole Riparto
          </TabsTrigger>
        </TabsList>

        {/* Tables Tab */}
        <TabsContent value="tables" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Tables List */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Elenco Tabelle</CardTitle>
                  {canEdit && (
                    <Dialog open={isNewTableOpen} onOpenChange={setIsNewTableOpen}>
                      <DialogTrigger asChild>
                        <Button size="sm">
                          <Plus className="h-4 w-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Nuova Tabella Millesimale</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleCreateTable} className="space-y-4">
                          <div className="space-y-2">
                            <Label>Nome *</Label>
                            <Input
                              value={tableForm.name}
                              onChange={(e) => setTableForm({ ...tableForm, name: e.target.value })}
                              placeholder="Es: Tabella Generale, Tabella Scale..."
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Descrizione</Label>
                            <Textarea
                              value={tableForm.description}
                              onChange={(e) => setTableForm({ ...tableForm, description: e.target.value })}
                              placeholder="Descrizione opzionale..."
                            />
                          </div>
                          <DialogFooter>
                            <Button type="submit">Crea Tabella</Button>
                          </DialogFooter>
                        </form>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {loading ? (
                  <p className="text-muted-foreground text-sm">Caricamento...</p>
                ) : tables.length === 0 ? (
                  <p className="text-muted-foreground text-sm">Nessuna tabella</p>
                ) : (
                  tables.map((table) => {
                    const statusInfo = TABLE_STATUS[table.status] || TABLE_STATUS.DRAFT;
                    return (
                      <div
                        key={table.id}
                        onClick={() => setSelectedTable(table)}
                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                          selectedTable?.id === table.id
                            ? 'border-slate-900 bg-slate-50'
                            : 'hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm">{table.name}</p>
                            <p className="text-xs text-muted-foreground">v{table.version}</p>
                          </div>
                          <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
                        </div>
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>

            {/* Table Editor */}
            <Card className="lg:col-span-2">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">
                      {selectedTable ? selectedTable.name : 'Seleziona una tabella'}
                    </CardTitle>
                    {selectedTable && (
                      <CardDescription>
                        Versione {selectedTable.version} • 
                        {selectedTable.valid_from && ` Dal ${selectedTable.valid_from}`}
                      </CardDescription>
                    )}
                  </div>
                  {selectedTable && userRole === 'ADMIN' && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDuplicateTable(selectedTable.id)}
                        title="Duplica tabella"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      {selectedTable.status === 'DRAFT' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handlePopulateTable}
                            title="Popola con tutte le unità"
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Popola
                          </Button>
                          <Dialog open={isPublishOpen} onOpenChange={setIsPublishOpen}>
                            <DialogTrigger asChild>
                              <Button size="sm" disabled={!isValid}>
                                <FileCheck className="h-4 w-4 mr-1" />
                                Pubblica
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Pubblica Tabella</DialogTitle>
                                <DialogDescription>
                                  La pubblicazione renderà attiva questa tabella e archivierà le versioni precedenti.
                                </DialogDescription>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div className="space-y-2">
                                  <Label>Data di Validità *</Label>
                                  <Input
                                    type="date"
                                    value={publishDate}
                                    onChange={(e) => setPublishDate(e.target.value)}
                                  />
                                </div>
                              </div>
                              <DialogFooter>
                                <Button variant="outline" onClick={() => setIsPublishOpen(false)}>
                                  Annulla
                                </Button>
                                <Button onClick={handlePublishTable}>Pubblica</Button>
                              </DialogFooter>
                            </DialogContent>
                          </Dialog>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteTable(selectedTable.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {!selectedTable ? (
                  <p className="text-center text-muted-foreground py-8">
                    Seleziona una tabella per visualizzare i dettagli
                  </p>
                ) : (
                  <div className="space-y-4">
                    {/* Validation Status */}
                    <div className={`p-3 rounded-lg flex items-center gap-3 ${
                      isValid ? 'bg-green-50 text-green-800' : 'bg-yellow-50 text-yellow-800'
                    }`}>
                      {isValid ? (
                        <CheckCircle className="h-5 w-5" />
                      ) : (
                        <AlertTriangle className="h-5 w-5" />
                      )}
                      <div>
                        <p className="font-medium">
                          Totale: {totalMillesimi.toFixed(2)} / 1000
                        </p>
                        <p className="text-xs">
                          {isValid
                            ? 'Tabella valida - pronta per la pubblicazione'
                            : `Differenza: ${(1000 - totalMillesimi).toFixed(2)} millesimi`}
                        </p>
                      </div>
                    </div>

                    {/* Add Entry Button */}
                    {canEditEntries && (
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setIsImportOpen(true)}
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Importa Excel
                        </Button>
                        <Dialog open={isAddEntryOpen} onOpenChange={setIsAddEntryOpen}>
                          <DialogTrigger asChild>
                            <Button variant="outline" size="sm">
                              <Plus className="h-4 w-4 mr-2" />
                              Aggiungi Unità
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Aggiungi Unità</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleAddEntry} className="space-y-4">
                              <div className="space-y-2">
                                <Label>Unità *</Label>
                                <Select
                                  value={newEntryForm.unit_id || undefined}
                                  onValueChange={(v) => setNewEntryForm({ ...newEntryForm, unit_id: v })}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Seleziona unità..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {units
                                      .filter(u => u.id && u.id.trim && u.id.trim() !== '' && !tableEntries.some(e => e.unit_id === u.id))
                                      .map((u) => (
                                        <SelectItem key={u.id} value={u.id}>
                                          {u.unit_number}
                                        </SelectItem>
                                      ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-2">
                                <Label>Millesimi *</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={newEntryForm.millesimi}
                                  onChange={(e) => setNewEntryForm({ ...newEntryForm, millesimi: e.target.value })}
                                  placeholder="0.00"
                                  required
                                />
                              </div>
                              <DialogFooter>
                                <Button type="submit" disabled={!newEntryForm.unit_id}>Aggiungi</Button>
                              </DialogFooter>
                            </form>
                          </DialogContent>
                        </Dialog>
                      </div>
                    )}

                    {/* Entries Table */}
                    <div className="max-h-96 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Unità</TableHead>
                            <TableHead className="text-right">Millesimi</TableHead>
                            <TableHead className="text-right">%</TableHead>
                            {canEditEntries && <TableHead className="w-20"></TableHead>}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {tableEntries.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4} className="text-center text-muted-foreground">
                                Nessuna voce. Usa &quot;Popola&quot; per aggiungere tutte le unità.
                              </TableCell>
                            </TableRow>
                          ) : (
                            tableEntries.map((entry) => (
                              <TableRow key={entry.id}>
                                <TableCell className="font-medium">{entry.unit_number}</TableCell>
                                <TableCell className="text-right">
                                  {editingEntryId === entry.id ? (
                                    <Input
                                      type="number"
                                      step="0.01"
                                      value={editingValue}
                                      onChange={(e) => setEditingValue(e.target.value)}
                                      className="w-24 text-right"
                                      autoFocus
                                    />
                                  ) : (
                                    entry.millesimi.toFixed(2)
                                  )}
                                </TableCell>
                                <TableCell className="text-right text-muted-foreground">
                                  {(entry.millesimi / 10).toFixed(2)}%
                                </TableCell>
                                {canEditEntries && (
                                  <TableCell className="text-right">
                                    {editingEntryId === entry.id ? (
                                      <div className="flex gap-1 justify-end">
                                        <Button
                                          size="icon"
                                          variant="ghost"
                                          onClick={() => handleUpdateEntry(entry.id)}
                                        >
                                          <Save className="h-4 w-4 text-green-600" />
                                        </Button>
                                        <Button
                                          size="icon"
                                          variant="ghost"
                                          onClick={() => setEditingEntryId(null)}
                                        >
                                          <X className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    ) : (
                                      <div className="flex gap-1 justify-end">
                                        <Button
                                          size="icon"
                                          variant="ghost"
                                          onClick={() => {
                                            setEditingEntryId(entry.id);
                                            setEditingValue(entry.millesimi.toString());
                                          }}
                                        >
                                          <Edit2 className="h-4 w-4" />
                                        </Button>
                                        <Button
                                          size="icon"
                                          variant="ghost"
                                          onClick={() => handleDeleteEntry(entry.id)}
                                        >
                                          <Trash2 className="h-4 w-4 text-red-600" />
                                        </Button>
                                      </div>
                                    )}
                                  </TableCell>
                                )}
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Allocation Rules Tab */}
        <TabsContent value="rules" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Regole di Riparto</h2>
            {userRole === 'ADMIN' && (
              <Dialog open={isRuleDialogOpen} onOpenChange={setIsRuleDialogOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="mr-2 h-4 w-4" />
                    Nuova Regola
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nuova Regola di Riparto</DialogTitle>
                    <DialogDescription>
                      Associa una categoria di spesa a una tabella millesimale
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateRule} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Categoria Spesa *</Label>
                      <Select
                        value={ruleForm.expense_category || undefined}
                        onValueChange={(v) => setRuleForm({ ...ruleForm, expense_category: v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleziona categoria..." />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(EXPENSE_CATEGORIES)
                            .filter(([k]) => k && k.trim() !== '')
                            .map(([k, v]) => (
                              <SelectItem key={k} value={k}>{v}</SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Tabella Millesimale *</Label>
                      {tables.filter(t => t.status === 'ACTIVE').length === 0 ? (
                        <p className="text-sm text-amber-600 p-2 bg-amber-50 rounded">
                          Nessuna tabella millesimale attiva. Pubblica prima una tabella.
                        </p>
                      ) : (
                        <Select
                          value={ruleForm.table_id || undefined}
                          onValueChange={(v) => setRuleForm({ ...ruleForm, table_id: v })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleziona tabella..." />
                          </SelectTrigger>
                          <SelectContent>
                            {tables
                              .filter(t => t.status === 'ACTIVE' && t.id && t.id.trim() !== '')
                              .map((t) => (
                                <SelectItem key={t.id} value={t.id}>
                                  {t.name} (v{t.version})
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Descrizione</Label>
                      <Input
                        value={ruleForm.description}
                        onChange={(e) => setRuleForm({ ...ruleForm, description: e.target.value })}
                        placeholder="Note opzionali..."
                      />
                    </div>
                    <DialogFooter>
                      <Button 
                        type="submit" 
                        disabled={!ruleForm.expense_category || !ruleForm.table_id}
                      >
                        Crea Regola
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          <Card>
            <CardContent className="pt-6">
              {allocationRules.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Nessuna regola definita. Le regole permettono di associare automaticamente 
                  le spese alle tabelle millesimali.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Categoria Spesa</TableHead>
                      <TableHead>Tabella Millesimale</TableHead>
                      <TableHead>Descrizione</TableHead>
                      {userRole === 'ADMIN' && <TableHead className="w-16"></TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allocationRules.map((rule) => (
                      <TableRow key={rule.id}>
                        <TableCell className="font-medium">
                          {EXPENSE_CATEGORIES[rule.expense_category] || rule.expense_category}
                        </TableCell>
                        <TableCell>{rule.table_name}</TableCell>
                        <TableCell className="text-muted-foreground">{rule.description || '-'}</TableCell>
                        {userRole === 'ADMIN' && (
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteRule(rule.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Excel Import Dialog */}
      <ExcelImportDialog
        open={isImportOpen}
        onOpenChange={setIsImportOpen}
        importType="millesimi"
        condominiumId={condoId}
        tableId={selectedTable?.id}
        onSuccess={() => {
          if (selectedTable) {
            fetchTableDetails(selectedTable.id);
          }
        }}
      />
    </div>
  );
};
